import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../colors";
import DarkHeroPageEffects from "../DarkHeroPageEffects";
import { PageContainer } from "../PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
  mb: { xs: 6, md: 8 },
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow: "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": { transform: "translateX(120%)" },
  "&:active": { transform: "translateY(0px)" },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";

const PILLARS = [
  {
    num: "01",
    name: "Raise the entry bar",
    body: "Verification makes it harder for throwaway accounts and repeat offenders to move through the system unnoticed. The door isn't locked — but it isn't wide open either.",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    num: "02",
    name: "Human review",
    body: "Not every trust decision should be hidden behind automation. Moderation and review remain part of the process wherever real judgment is needed — not just pattern-matching.",
    icon: (
      <>
        <circle cx="12" cy="8" r="5" />
        <path d="M3 21v-1a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8v1" />
      </>
    ),
  },
  {
    num: "03",
    name: "Visible policy",
    body: "Rules around conduct, verification, and access should be readable before a player invests time into the platform. No buried terms. No surprises when enforcement happens.",
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 9h6M9 13h6M9 17h4" />
      </>
    ),
  },
  {
    num: "04",
    name: "Accountable reports",
    body: "Reports carry consequences for accuracy — in both directions. This protects honest players from noise while keeping bad-faith reporting from becoming a weapon.",
    icon: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />,
  },
];

const VERIFY_POINTS = [
  {
    title: "Steam account linking",
    body: "Read-only Steam authentication ties your Ouro account to a real game history. No inventory access, no trade permissions — just identity confirmation and play history context.",
    icon: <path d="M8 1l2.06 4.17L15 6.18l-3.5 3.41.83 4.82L8 12.17l-4.33 2.24.83-4.82L1 6.18l4.94-.01L8 1z" />,
  },
  {
    title: "History context import",
    body: "FaceIT-related history and competitive context can be imported during setup, giving your starting reputation a more accurate baseline than a blank slate.",
    icon: (
      <>
        <path d="M13 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
        <path d="M5 8l2 2 4-4" />
      </>
    ),
  },
  {
    title: "Permanent account binding",
    body: "Reputation is permanently tied to your account. You can't escape a poor record by starting fresh. Verification is what makes this possible — and meaningful.",
    icon: (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 5v3l2 1" />
      </>
    ),
  },
  {
    title: "Re-entry prevention",
    body: "Banned accounts face barriers to re-entry that go beyond a simple IP block. Verification is the mechanism that makes those barriers work.",
    icon: <path d="M8 14s-6-3-6-7V4l6-2 6 2v3c0 4-6 7-6 7z" />,
  },
];

const VERIFY_STEPS = [
  { title: "Sign in with Steam", body: "Read-only OAuth. Game library and match history is accessible; nothing else." },
  { title: "Complete your profile", body: "Region, competitive context, and optional history import from FaceIT." },
  { title: "Pass verification check", body: "Account age, play history, and standing reviewed against platform thresholds." },
  { title: "Reputation baseline set", body: "You start at 50 across all six dimensions. Your record begins here." },
  { title: "Account permanently bound", body: "From this point, your reputation follows you. It can't be reset or abandoned." },
];

const MODERATION_SLA = [
  { cat: "Cheating — confirmed evidence", val: "24 hrs", gold: true },
  { cat: "Harassment — documented pattern", val: "48 hrs", gold: true },
  { cat: "Match abandonment — first offence", val: "72 hrs" },
  { cat: "False report investigation", val: "72 hrs" },
  { cat: "Account appeal review", val: "7 days" },
  { cat: "Complex multi-report cases", val: "Case-by-case" },
];

const MODERATION_POINTS = [
  {
    title: "Admin-reviewed reports",
    body: "Every validated report is reviewed by a human before any reputation consequence applies. There are no purely algorithmic bans during the founding period.",
    icon: (
      <>
        <circle cx="8" cy="5" r="3" />
        <path d="M2 14v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
      </>
    ),
  },
  {
    title: "Proportional consequences",
    body: "First offences, patterns, and severe violations are treated differently. Moderation isn't a single strike — it's a graduated system that fits the severity of what happened.",
    icon: <path d="M8 1v14M1 8h14" />,
  },
  {
    title: "Decision notifications",
    body: "When a report you filed reaches a decision, you hear about it. Moderation doesn't happen silently — outcomes are communicated to both parties where appropriate.",
    icon: <path d="M2 4l6 4 6-4M2 4v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4" />,
  },
  {
    title: "Right of appeal",
    body: "Decisions can be appealed. The appeal process is reviewed by a different admin than the one who made the original call — with a target of seven days for a response.",
    icon: <path d="M13 6H3a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1zM5 6V4a3 3 0 0 1 6 0v2" />,
  },
];

const REPORTING_POINTS = [
  {
    title: "Every report is reviewed",
    body: "No report goes straight to an automated penalty. Every submission enters an admin queue and is assessed against evidence, match data, and player history.",
    icon: <path d="M14 9.33V14H2V9.33M8 2v8M5 5l3-3 3 3" />,
  },
  {
    title: "Outcomes affect both sides",
    body: "A validated report improves the reporter's quality score and penalises the reported player. A false report does the reverse. This loop is what makes the system resistant to abuse.",
    icon: <path d="M8 2l2.06 4.17L15 7.18l-3.5 3.41.83 4.82L8 13.17l-4.33 2.24.83-4.82L1 7.18l4.94-.01L8 2z" />,
  },
  {
    title: "Three possible outcomes",
    body: "Validated — the reported player receives a penalty and the reporter gains quality points. Dismissed — no penalty, reporter loses one point. False — reporter penalised, reported player compensated.",
    icon: <path d="M4 8h8M8 4l4 4-4 4" />,
  },
  {
    title: "Evidence matters",
    body: "Reports filed with screenshots, video clips, or specific match references are reviewed faster and weighed more heavily. Vague reports are still assessed — but context helps.",
    icon: <path d="M14 2H9L7 4H2a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />,
  },
];

const REPORT_FLOW = [
  "Player files a report with description and any evidence after a match.",
  "Report enters the admin review queue — no automated penalty applied yet.",
  "Admin reviews evidence, match data, and both players' histories.",
  "Decision is reached: Validated, Dismissed, or False Report.",
  "Reputation adjustments applied to both players based on outcome.",
  "Both parties notified of outcome. Appeal window opens for reported player.",
];

const CONDUCT_ROWS = [
  { name: "Cheating / third-party exploit use", sev: "Critical", width: "100%", color: colors.state.danger.main },
  { name: "Targeted harassment or abuse", sev: "Critical", width: "100%", color: colors.state.danger.main },
  { name: "Match abandonment", sev: "High", width: "78%", color: "#E0934A" },
  { name: "Intentional feeding or griefing", sev: "High", width: "78%", color: "#E0934A" },
  { name: "Toxic chat or voice behaviour", sev: "Medium", width: "52%", color: colors.primary.main },
  { name: "False reporting (confirmed)", sev: "Medium", width: "52%", color: colors.primary.main },
  { name: "Idle / AFK behaviour in match", sev: "Low", width: "25%", color: colors.text.muted },
];

const POLICY_POINTS = [
  {
    title: "Conduct standards apply to everyone",
    body: "There are no exceptions for high reputation, long tenure, or paid membership. The same standards apply to every verified player, regardless of tier.",
    icon: <path d="M8 2l2.06 4.17L15 7.18l-3.5 3.41.83 4.82L8 13.17l-4.33 2.24.83-4.82L1 7.18l4.94-.01L8 2z" />,
  },
  {
    title: "Policy is published, not inferred",
    body: "What's acceptable and what isn't should be readable before you invest time into the platform — not discovered when enforcement happens for the first time.",
    icon: (
      <>
        <rect x="2" y="2" width="12" height="12" rx="1" />
        <path d="M6 8l2 2 4-4" />
      </>
    ),
  },
  {
    title: "Policy evolves with the platform",
    body: "As Ouro grows, edge cases will emerge. Policy updates are communicated to players in advance. Retroactive enforcement for new rules is not how this works.",
    icon: (
      <>
        <path d="M12 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM6 6h4M6 9h2" />
      </>
    ),
  },
];

const EXPECT_CARDS = [
  {
    title: "A better player pool",
    body: "Verification exists to protect the quality of the people you queue with. Every player in the pool went through the same entry process you did. That's not nothing.",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    title: "Accountability that holds",
    body: "Reports are reviewed. Outcomes are communicated. Appeals are considered. The system doesn't disappear once you hit submit — it follows through.",
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    title: "Systems that reinforce each other",
    body: "Verification, reputation, reporting, and anti-cheat aren't separate features. They're one interconnected layer — designed so that a failure in one has backstops in the others.",
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </>
    ),
  },
  {
    title: "Nothing buried in the fine print",
    body: "Conduct standards are written to be readable, not to protect Ouro legally. If a rule exists, it's because it protects the player pool — not because of a legal requirement.",
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 9h6M9 13h6M9 17h4" />
      </>
    ),
  },
  {
    title: "Early access means real influence",
    body: "The founding community has meaningful input on how these systems develop. If something isn't working, the channel to say so is short — and it's taken seriously.",
    icon: (
      <>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </>
    ),
  },
  {
    title: "Support when it matters",
    body: "If you believe a moderation decision was wrong, you can appeal it. If you need to flag something urgent, there's a direct route to the admin team — not a chatbot.",
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
];

const SEV_STYLES = {
  Critical: { color: colors.state.danger.main, bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.25)" },
  High: { color: "#E0934A", bg: "rgba(224,147,74,0.1)", border: "rgba(224,147,74,0.25)" },
  Medium: { color: colors.primary.main, bg: "rgba(232,163,61,0.1)", border: "rgba(232,163,61,0.25)" },
  Low: { color: colors.text.muted, bg: "rgba(115,115,125,0.3)", border: "rgba(115,115,125,0.5)" },
};

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>{title}</Typography>
      {description ? <Typography sx={descSx}>{description}</Typography> : null}
    </MotionBox>
  );
}

function PointIcon({ children }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      sx={{ width: 16, height: 16, flexShrink: 0, mt: "2px", color: colors.primary.main }}
    >
      {children}
    </Box>
  );
}

function LayerPoints({ points }) {
  return (
    <Box>
      {points.map((point) => (
        <Box
          key={point.title}
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            py: 2,
            borderBottom: `1px solid ${colors.border.subtle}`,
            "&:last-of-type": { borderBottom: "none" },
          }}
        >
          <PointIcon>{point.icon}</PointIcon>
          <Box>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>
              {point.title}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.6 }}>
              {point.body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function LayerPanel({ label, children }) {
  return (
    <Box
      sx={{
        ...cardSx,
        p: { xs: "28px 24px", md: "36px" },
        transition: "border-color .3s",
        "&:hover": { borderColor: "rgba(255,255,255,0.08)" },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.5625rem",
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: colors.text.muted,
          mb: 3,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function LayerGrid({ flip, left, right }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 5, md: 8 },
        alignItems: "start",
        direction: { xs: "ltr", md: flip ? "rtl" : "ltr" },
        "& > *": { direction: "ltr" },
      }}
    >
      <Box>{left}</Box>
      <Box>{right}</Box>
    </Box>
  );
}

function ConductBar({ width, color }) {
  return (
    <Box sx={{ height: 2, background: colors.background.surface }}>
      <MotionBox
        initial={{ width: 0 }}
        whileInView={{ width }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        sx={{ height: "100%", background: color }}
      />
    </Box>
  );
}

export default function TrustAndSafetyPage() {
  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <DarkHeroPageEffects watermark="TRUST & SAFETY" />

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow>Trust &amp; Safety</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 860,
              mb: 4,
            }}
          >
            Trust isn&apos;t
            <br />
            a feature.
            <br />
            It&apos;s the{" "}
            <Box component="span" sx={{ background: GOLD_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              foundation.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32rem",
                mb: 6,
              }}
            >
              Cleaner matches don&apos;t happen by accident. They come from better entry standards, visible accountability systems, and enforcement that works in both directions.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the founding community
            </Box>
            <Box component={Link} to="/anti-cheat" sx={btnGhostSx}>
              How anti-cheat works →
            </Box>
          </MotionBox>
        </Box>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="The trust layer"
            title="Four jobs. One goal."
            description="Every part of Ouro's trust system exists to do one thing: make sure that the person queueing next to you actually deserves to be there."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: "2px",
            }}
          >
            {PILLARS.map((pillar) => (
              <Box
                key={pillar.num}
                sx={{
                  ...cardSx,
                  borderRadius: 0,
                  p: { xs: "28px 24px", md: "36px 28px" },
                  position: "relative",
                  overflow: "hidden",
                  transition: "border-color .3s",
                  "&:hover": { borderColor: "rgba(232,163,61,0.3)" },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: colors.primary.main,
                  },
                }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    right: 20,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "3.75rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.03)",
                    lineHeight: 1,
                  }}
                >
                  {pillar.num}
                </Typography>
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  sx={{ width: 32, height: 32, mb: 2.5, color: colors.primary.main }}
                >
                  {pillar.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.0625rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {pillar.name}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  {pillar.body}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Layer 01"
            title="Verification."
            description="Anyone can create an account. Not everyone gets through the door. Verification is the first filter — and it's the only way to make the player pool worth protecting."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <LayerGrid
              left={<LayerPoints points={VERIFY_POINTS} />}
              right={
                <LayerPanel label="Verification flow">
                  {VERIFY_STEPS.map((step, i) => (
                    <Box
                      key={step.title}
                      sx={{
                        display: "flex",
                        gap: 2,
                        py: 2,
                        borderBottom: i < VERIFY_STEPS.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          color: colors.primary.main,
                          flexShrink: 0,
                          width: 20,
                          pt: "2px",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Typography>
                      <Box>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: colors.text.primary, mb: 0.25 }}>
                          {step.title}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: colors.text.secondary, lineHeight: 1.6 }}>
                          {step.body}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </LayerPanel>
              }
            />
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Layer 02"
            title="Moderation."
            description="Automation can flag patterns. It can't read intent. Human review stays in the loop for anything where judgment actually matters."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <LayerGrid
              flip
              left={
                <LayerPanel label="Review response targets">
                  {MODERATION_SLA.map((row, i) => (
                    <Box
                      key={row.cat}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        py: 1.75,
                        borderBottom: i < MODERATION_SLA.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, pr: 2 }}>
                        {row.cat}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          color: row.gold ? colors.primary.main : colors.text.primary,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.val}
                      </Typography>
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: "0.6875rem", color: colors.text.muted, mt: 2.5, lineHeight: 1.6 }}>
                    Response targets are aspirational during the founding period and will tighten as the admin team scales with player volume.
                  </Typography>
                </LayerPanel>
              }
              right={<LayerPoints points={MODERATION_POINTS} />}
            />
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Layer 03"
            title="Reporting."
            description="Reports that cost nothing to file tend to be filed for everything. Ouro's reporting system works in both directions — which keeps it sharp, and keeps it fair."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <LayerGrid
              left={<LayerPoints points={REPORTING_POINTS} />}
              right={
                <LayerPanel label="Report lifecycle">
                  {REPORT_FLOW.map((text, i) => (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        gap: 2,
                        py: 1.75,
                        borderBottom: i < REPORT_FLOW.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          color: colors.primary.main,
                          flexShrink: 0,
                          width: 20,
                          pt: "2px",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.6 }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </LayerPanel>
              }
            />
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Layer 04"
            title="Policy."
            description="What's allowed on Ouro isn't buried in a terms document. These are the conduct standards the platform enforces — ordered by severity."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <LayerGrid
              flip
              left={
                <LayerPanel label="Conduct severity levels">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {CONDUCT_ROWS.map((row) => {
                      const sev = SEV_STYLES[row.sev];
                      return (
                        <Box key={row.name}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: colors.text.secondary }}>
                              {row.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.5625rem",
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                px: 1,
                                py: 0.25,
                                color: sev.color,
                                background: sev.bg,
                                border: `1px solid ${sev.border}`,
                              }}
                            >
                              {row.sev}
                            </Typography>
                          </Box>
                          <ConductBar width={row.width} color={row.color} />
                        </Box>
                      );
                    })}
                  </Box>
                </LayerPanel>
              }
              right={<LayerPoints points={POLICY_POINTS} />}
            />
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="What to expect"
            title="What this means for you."
            description="Trust and safety isn't only about stopping bad actors. It shapes who's in the pool, how confidence is built over time, and whether accountability actually holds."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: "2px",
            }}
          >
            {EXPECT_CARDS.map((card) => (
              <Box
                key={card.title}
                sx={{
                  ...cardSx,
                  borderRadius: 0,
                  p: { xs: "28px 24px", md: "36px 32px" },
                  transition: "border-color .3s",
                  "&:hover": { borderColor: "rgba(232,163,61,0.3)" },
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  sx={{ width: 28, height: 28, mb: 2.5, color: colors.primary.main }}
                >
                  {card.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  {card.body}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={{ ...sectionSx, pb: 0 }}>
        <PageContainer sx={{ maxWidth: "75rem", pb: { xs: "80px", md: "120px" } }}>
          <SectionIntro
            label="The full picture"
            title="Trust, reputation, anti-cheat."
            description="These aren't three separate systems. They're one interconnected layer — designed so that a failure in one area has backstops in the others."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              ...cardSx,
              borderColor: "rgba(232,163,61,0.3)",
              background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
              p: { xs: "36px 28px", md: "56px 64px" },
              position: "relative",
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 0,
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "35%",
                height: "1px",
                background: `linear-gradient(90deg, transparent, ${colors.primary.main}, transparent)`,
              }}
            />
            {[
              {
                name: "Trust & Safety",
                body: "Verification, moderation, reporting, and policy. The entry filter and the accountability loop that keeps it intact after players are in.",
                link: null,
                linkLabel: "You are here",
                active: true,
              },
              {
                name: "Reputation",
                body: "A permanent record across six dimensions. Builds over every match, every report, every commendation. The long-term picture of how you actually play.",
                to: "/reputation",
                linkLabel: "See reputation →",
              },
              {
                name: "Anti-Cheat",
                body: "The technical enforcement layer. Works alongside trust and reputation to catch what verification and reports can't — and feeds outcomes back into the broader system.",
                to: "/anti-cheat",
                linkLabel: "See anti-cheat →",
              },
            ].map((item, i) => (
              <Box
                key={item.name}
                sx={{
                  py: { xs: 3, md: 0 },
                  px: { md: i === 0 ? 0 : 5 },
                  borderRight: { md: i < 2 ? `1px solid ${colors.border.subtle}` : "none" },
                  borderBottom: { xs: i < 2 ? `1px solid ${colors.border.subtle}` : "none", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: colors.primary.main,
                    mb: 1.25,
                  }}
                >
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.7, mb: 2 }}>
                  {item.body}
                </Typography>
                {item.active ? (
                  <Typography
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: colors.primary.main,
                    }}
                  >
                    {item.linkLabel}
                  </Typography>
                ) : (
                  <Box
                    component={Link}
                    to={item.to}
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      transition: "color .2s",
                      "&:hover": { color: colors.primary.main },
                    }}
                  >
                    {item.linkLabel}
                  </Box>
                )}
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              Built on
              <br />
              stronger
              <br />
              standards.
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              Join the founding community and be part of building the player pool these systems are designed to protect.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the founding community
            </Box>
            <Box component={Link} to="/anti-cheat" sx={btnGhostSx}>
              How anti-cheat works →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
